"use client"

import { useForm, type SubmitHandler } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import type { components } from "umbraco"
import { postEggedPlusDriverCareerSubmit } from "api"
import {
  FormShell,
  FormTextField,
  FormCheckboxField,
  FieldRow,
  FieldCol,
  useFormSubmit,
  checkBooleanResponse,
  validateRecipients,
  RecipientsConfigError,
  getShellLabels,
  type FormLanguage,
} from "../../common/forms"
import { createSchema, type FormValues, defaultValues } from "./formSchema"

type DriverCareerFormData =
  components["schemas"]["DriverCareerFormPropertiesModel"]

function getFieldLabels(locale: string) {
  if (locale === "en") {
    return { fullName: "Full Name", phone: "Phone", city: "City of Residence" }
  }
  return { fullName: "שם מלא", phone: "טלפון", city: "עיר מגורים" }
}

interface Props {
  data: DriverCareerFormData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

export default function DriverCareerForm({ data, locale }: Props) {
  const shellLabels = getShellLabels(locale)
  const fieldLabels = getFieldLabels(locale)
  const recipients = data.recipients ?? []
  const showCheckbox = data.showCheckbox ?? false
  const checkboxRequired = showCheckbox && (data.checkboxRequired ?? false)

  const schema = createSchema(locale as FormLanguage, checkboxRequired)

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues,
    mode: "onBlur",
  })

  const cultureLocale =
    locale === "he" ? "he-IL" : locale === "en" ? "en-US" : locale

  const { state, isPending, submit, retry } = useFormSubmit<FormValues>({
    mutationFn: async (formData) =>
      checkBooleanResponse(
        await postEggedPlusDriverCareerSubmit(
          {
            fullName: formData.fullName,
            phone: formData.phone,
            cityOfResidence: formData.cityOfResidence,
            // EGDE-445: only send the consent value when the checkbox is
            // actually shown — a hidden checkbox must not arrive as "לא" in
            // the email. `undefined` is dropped from the JSON body.
            ...(showCheckbox
              ? { confirmationCheckbox: !!formData.checkbox }
              : {}),
            referrerUrl: window.location.href,
            recipients,
            attachment: { fileName: "", base64Content: "" },
          } as Parameters<typeof postEggedPlusDriverCareerSubmit>[0],
          cultureLocale,
        ),
      ),
    onReset: reset,
  })

  if (!validateRecipients(recipients)) {
    return <RecipientsConfigError formName="DriverCareerForm" />
  }

  const onSubmit: SubmitHandler<FormValues> = (formData) => submit(formData)

  return (
    <FormShell
      title={data.title}
      subtitle={data.subtitle}
      labels={shellLabels}
      state={state}
      isPending={isPending}
      onSubmit={handleSubmit(onSubmit)}
      onRetry={retry}
      footer={
        showCheckbox ? (
          <FormCheckboxField
            control={control}
            name="checkbox"
            markup={data.checkboxText?.markup}
            required={checkboxRequired}
            error={errors.checkbox?.message}
          />
        ) : null
      }
    >
      <FieldRow>
        <FieldCol>
          <FormTextField
            control={control}
            name="fullName"
            label={fieldLabels.fullName}
            required
            autoComplete="name"
            error={errors.fullName?.message}
          />
        </FieldCol>
        <FieldCol>
          <FormTextField
            control={control}
            name="phone"
            label={fieldLabels.phone}
            required
            type="tel"
            autoComplete="tel"
            error={errors.phone?.message}
          />
        </FieldCol>
      </FieldRow>

      <FieldCol>
        <FormTextField
          control={control}
          name="cityOfResidence"
          label={fieldLabels.city}
          required
          autoComplete="address-level2"
          error={errors.cityOfResidence?.message}
        />
      </FieldCol>
    </FormShell>
  )
}
